/**
 * What the bell has already been shown for, for the rows nobody stores.
 *
 * Most of what the bell lists is a condition that resolves itself: an overdue
 * invoice stops being overdue when somebody pays it (see `signals.ts`). Storing
 * such a row on the server would mean a second source of truth that goes stale
 * the moment it is settled in another tab, so those counts are derived live on
 * every poll and this file is the small, honest counterpart: a record of the
 * count each row had when the operator last cleared it. Nothing is claimed about
 * the server, and nothing here can contradict it.
 *
 * The count, not a timestamp, is the thing worth storing for those rows. Two
 * overdue invoices cleared at two stay cleared; a third one brings the row
 * straight back. A "seen until" timestamp would swallow the third as well, and
 * hiding a new fact is not the same as having read the old one - which is
 * exactly why the button is labelled for what it does rather than "mark all
 * read".
 *
 * Two rows are NOT like that. A missed call and a voicemail happened, and
 * nothing un-happens them; those arrive already filtered by a per-operator
 * cursor the server holds (`lib/callSeen.ts`), carry `tracked: true`, and this
 * file deliberately keeps its hands off them. Applying both notions of seen to
 * one row means the stricter always wins, and here the stricter one would hide a
 * row that has just been reset to zero for good.
 *
 * The store is namespaced by user id. It used to be one key for the browser,
 * which was fine while it only hid resolving conditions and is not fine now:
 * two operators at one workstation would read each other's calls.
 */
import type { SignalRow } from "./signals";

const KEY_PREFIX = "crm.alerts.cleared";

export type SeenCounts = Record<string, number>;

/** One operator's slot. An empty id would collapse everybody back into one
 *  bucket, so it gets its own rather than the bare prefix. */
function keyFor(userId: string): string {
  return `${KEY_PREFIX}.${userId || "anon"}`;
}

/** Storage can be unavailable (private windows, blocked third-party contexts).
 *  An unreadable store means nothing is cleared, which shows more rather than
 *  less - the safe direction for a notification. */
export function readSeen(userId: string): SeenCounts {
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: SeenCounts = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === "number" && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/** Records every untracked row currently listed at the count it currently has.
 *  Rows that are not listed are dropped, so the store cannot grow forever on
 *  keys that stopped existing. Tracked rows are not written: zeroing those is
 *  the server's job, and a stale number here would outlive it. */
export function clearSignals(rows: SignalRow[], userId: string): SeenCounts {
  const next: SeenCounts = {};
  for (const row of rows) {
    if (row.tracked) continue;
    next[row.key] = row.count;
  }
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(next));
  } catch {
    // Nothing was stored, so nothing is hidden on the next load either. The
    // returned value still hides the rows for this session.
  }
  return next;
}

/** The rows still worth showing: anything tracked (the server already decided),
 *  plus anything untracked that has grown since it was cleared. */
export function unseenSignals(rows: SignalRow[], seen: SeenCounts): SignalRow[] {
  return rows.filter((row) => row.tracked || (seen[row.key] ?? 0) < row.count);
}
